import {
  PLOT_DEVICE_CATEGORIES,
  SEEDED_PLOT_DEVICE_CATALOG,
} from '../domain/plotDeviceCatalog.js';
import type { PlotDevice } from '../domain/types.js';
import { estimateTokens } from './budget.js';

/** Compact category list from the 100-device seeded library. */
export function formatDeviceCategories(): string {
  return PLOT_DEVICE_CATEGORIES.join(', ');
}

/** Bounded taxonomy excerpt — categories always present; seeded rows trimmed to budget. */
export function formatBoundedDeviceTaxonomy(maxTokens: number): string {
  const categoryBlock = `DEVICE_CATEGORIES (${PLOT_DEVICE_CATEGORIES.length}): ${formatDeviceCategories()}`;
  let used = estimateTokens(categoryBlock);
  const lines: string[] = [];
  for (const d of SEEDED_PLOT_DEVICE_CATALOG) {
    const line = `${d.deviceId}|${d.category}|${d.title}`;
    const cost = estimateTokens(line);
    if (used + cost > maxTokens) break;
    lines.push(line);
    used += cost;
  }
  const truncated = lines.length < SEEDED_PLOT_DEVICE_CATALOG.length;
  const suffix = truncated ? ` …+${SEEDED_PLOT_DEVICE_CATALOG.length - lines.length} more` : '';
  return [
    categoryBlock,
    `SEEDED_LIBRARY (${SEEDED_PLOT_DEVICE_CATALOG.length}${truncated ? ', truncated' : ''}):`,
    lines.join('\n') + suffix,
  ].join('\n');
}

export function formatDeviceExclusionList(
  seeded: readonly PlotDevice[],
  priorGenerated: PlotDevice[],
  inactive: PlotDevice[] | undefined,
  maxTokens: number,
): string {
  const ids = [
    ...seeded.map((d) => d.deviceId),
    ...priorGenerated.map((d) => d.deviceId),
    ...(inactive ?? []).map((d) => d.deviceId),
  ];
  const unique = [...new Set(ids)].sort();
  const header = `EXCLUDE_DEVICE_IDS (${unique.length}) — do not duplicate:`;
  let body = unique.join(', ');
  while (body.length > 0 && estimateTokens(`${header}\n${body}`) > maxTokens) {
    const parts = body.split(', ');
    parts.pop();
    body = parts.length ? `${parts.join(', ')}, …` : '(truncated)';
  }
  return `${header}\n${body || '(none)'}`;
}
